import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { PettyCashService } from '../../services/pettyCash.service';
import { PERMISSIONS } from '../../constants/permissions';
import {
  FundDetail, PettyCashFund, ReplenishmentProposal,
} from '../../interfaces/treasury.interface';
import { BankAccount } from '../../interfaces/bank/bankAccount.interface';
import { Account, Segment } from '../../interfaces/accounting.interface';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { SearchableSelect } from '../common/SearchableSelect';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import EmployeeAutocomplete, { EmployeeOption } from '../common/EmployeeAutocomplete';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { helpElement } from '../../help/attrs';
import { FILTER_CELL, bankLabel, segmentLabel, useTreasuryRefData } from './treasuryRef';
import { ReceiptButtons, VoucherDialog } from './PettyCashParts';
import { departmentNameOf, useDepartmentOptions } from '../costcentre/DepartmentPicker'; // polish 4
import { projectLabel, useProjectOptions } from '../project/ProjectSelect'; // P1-A project

/** In-app help keys for the close-fund and void-replenishment confirm windows (literal so the key scan sees them). */
const PC_CONFIRM_HELP = {
  close: { paper: { 'data-help-page': 'bank.pcCloseFund.page' }, confirm: { 'data-help': 'bank.pcCloseFund.confirm' }, cancel: { 'data-help': 'bank.pcCloseFund.cancel' } },
  void: { paper: { 'data-help-page': 'bank.pcVoidReplenish.page' }, confirm: { 'data-help': 'bank.pcVoidReplenish.confirm' }, cancel: { 'data-help': 'bank.pcVoidReplenish.cancel' } },
};

const DENOMS = [1000, 500, 200, 100, 50, 20, 10, 5, 2, 1];

type Dlg =
  | { kind: 'create' }
  | { kind: 'edit' | 'replenish' | 'count' | 'voidVoucher'; fund: PettyCashFund; voucherId?: string };

/** Help keys of the petty-cash dialog, by kind (literals so the help key scan sees them). */
const PC_DLG_HELP: Record<Dlg['kind'], { paper: Record<string, string>; helpKey: string }> = {
  create: { paper: { 'data-help-page': 'bank.pcCreate.page' }, helpKey: 'bank.pcCreate.save' },
  edit: { paper: { 'data-help-page': 'bank.pcEdit.page' }, helpKey: 'bank.pcEdit.save' },
  replenish: { paper: { 'data-help-page': 'bank.pcReplenish.page' }, helpKey: 'bank.pcReplenish.save' },
  count: { paper: { 'data-help-page': 'bank.pcCount.page' }, helpKey: 'bank.pcCount.save' },
  voidVoucher: { paper: { 'data-help-page': 'bank.pcVoidVoucher.page' }, helpKey: 'bank.pcVoidVoucher.save' },
};

const PettyCash: React.FC = () => {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.BANK_ACCOUNT_MANAGE);
  const institutionId = AuthService.getSelectedInstitution()!;
  const departmentOptions = useDepartmentOptions(institutionId); // polish 4
  const projectOptions = useProjectOptions(institutionId); // P1-A project
  const projectName = (id?: string | null) => { const p = id ? projectOptions.find(o => o.id === id) : undefined; return p ? projectLabel(p) : (id ? id.slice(0, 8) : '—'); };
  const ref = useTreasuryRefData(e => toast.showApiError(e, 'Failed to load reference data'));
  const [funds, setFunds] = useState<PettyCashFund[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string>('');
  const [detail, setDetail] = useState<FundDetail | null>(null);
  const [dlg, setDlg] = useState<Dlg | null>(null);
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [custodian, setCustodian] = useState<EmployeeOption | null>(null);
  const [glId, setGlId] = useState('');
  const [floatAmount, setFloatAmount] = useState('');
  const [limit, setLimit] = useState('');
  const [segmentId, setSegmentId] = useState('');
  const [bankId, setBankId] = useState('');
  const [floatDate, setFloatDate] = useState(toLocalIsoDate());
  const [remarks, setRemarks] = useState('');
  const [reason, setReason] = useState('');
  const [proposal, setProposal] = useState<ReplenishmentProposal | null>(null);
  const [throughDate, setThroughDate] = useState(toLocalIsoDate());
  const [date, setDate] = useState(toLocalIsoDate());
  const [notes, setNotes] = useState('');
  const [counted, setCounted] = useState('');
  const [counts, setCounts] = useState<Record<number, string>>({});
  const [postAdj, setPostAdj] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setFunds(await PettyCashService.listFunds(institutionId));
    } catch (e) {
      toast.showApiError(e, 'Failed to load petty-cash funds');
    } finally {
      setLoading(false);
    }
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDetail = useCallback(async (id: string) => {
    if (!id) { setDetail(null); return; }
    try {
      setDetail(await PettyCashService.getFund(id));
    } catch (e) {
      toast.showApiError(e, 'Failed to load fund');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDetail(selected); }, [selected, loadDetail]);

  const refresh = async () => { await load(); await loadDetail(selected); };

  const denomTotal = DENOMS.reduce((s, d) => s + d * (Number(counts[d]) || 0), 0);
  const usedDenoms = DENOMS.some(d => Number(counts[d]) > 0);
  const countedTotal = usedDenoms ? denomTotal : Number(counted);

  const openDlg = (d: Dlg) => {
    setDlg(d); setReason(''); setNotes(''); setDate(toLocalIsoDate()); setProposal(null); setThroughDate(toLocalIsoDate());
    setCounted(''); setCounts({}); setPostAdj(false); setBankId('');
    if (d.kind === 'create') {
      setName(''); setCustodian(null); setGlId(''); setFloatAmount(''); setLimit(''); setSegmentId(''); setFloatDate(toLocalIsoDate()); setRemarks('');
    } else if (d.kind === 'edit') {
      setName(d.fund.name); setCustodian({ id: d.fund.custodianUserId, username: d.fund.custodianName ?? '', fullName: d.fund.custodianName ?? '' });
      setLimit(String(d.fund.perVoucherLimit)); setSegmentId(d.fund.segmentId ?? ''); setRemarks(d.fund.remarks ?? '');
    }
  };

  const loadProposal = useCallback(async (fundId: string, through: string) => {
    try {
      setProposal(await PettyCashService.proposal(fundId, through || undefined));
    } catch (e) {
      toast.showApiError(e, 'Failed to load replenishment proposal');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (dlg?.kind === 'replenish') loadProposal(dlg.fund.id, throughDate);
  }, [dlg, throughDate, loadProposal]);

  const valid = (() => {
    if (!dlg) return false;
    switch (dlg.kind) {
      case 'create': return !!name.trim() && !!custodian && !!glId && Number(floatAmount) > 0 && Number(limit) > 0;
      case 'edit': return !!name.trim() && !!custodian && Number(limit) > 0;
      case 'replenish': return !!bankId && !!proposal && proposal.amount > 0;
      case 'count': return countedTotal >= 0 && (usedDenoms || counted !== '');
      case 'voidVoucher': return reason.trim().length > 0;
      default: return false;
    }
  })();

  const save = async () => {
    if (!dlg) return;
    try {
      setSaving(true);
      if (dlg.kind === 'create') {
        await PettyCashService.createFund({
          institutionId, name: name.trim(), custodianUserId: custodian!.id, glAccountId: glId, floatAmount: Number(floatAmount),
          perVoucherLimit: Number(limit), segmentId: segmentId || undefined, fundingBankAccountId: bankId || undefined,
          floatDate: floatDate || undefined, remarks: remarks.trim() || undefined,
        });
      } else if (dlg.kind === 'edit') {
        await PettyCashService.updateFund(dlg.fund.id, {
          name: name.trim(), custodianUserId: custodian!.id, perVoucherLimit: Number(limit),
          segmentId: segmentId || undefined, remarks: remarks.trim() || undefined,
        });
      } else if (dlg.kind === 'replenish') {
        await PettyCashService.replenish({
          fundId: dlg.fund.id, bankAccountId: bankId, throughDate: throughDate || undefined, date: date || undefined,
          notes: notes.trim() || undefined,
        });
      } else if (dlg.kind === 'count') {
        const breakdown: Record<string, number> = {};
        DENOMS.forEach(d => { if (Number(counts[d]) > 0) breakdown[String(d)] = Number(counts[d]); });
        await PettyCashService.count({
          fundId: dlg.fund.id, countDate: date || undefined, countedTotal,
          denominations: usedDenoms ? JSON.stringify(breakdown) : undefined, postAdjustment: postAdj, notes: notes.trim() || undefined,
        });
      } else {
        await PettyCashService.voidVoucher(dlg.voucherId!, reason.trim());
      }
      toast.showSuccess('Saved');
      setDlg(null);
      await refresh();
    } catch (e) {
      toast.showApiError(e, 'Petty-cash action failed');
    } finally {
      setSaving(false);
    }
  };

  const [confirm, setConfirm] = useState<{ kind: 'close'; fund: PettyCashFund } | { kind: 'voidRepl'; id: string; number: string } | null>(null);
  const [confirming, setConfirming] = useState(false);

  const runConfirmed = async () => {
    if (!confirm || confirming) return;
    try {
      setConfirming(true);
      if (confirm.kind === 'close') {
        await PettyCashService.closeFund(confirm.fund.id);
        toast.showSuccess('Fund closed');
      } else {
        await PettyCashService.voidReplenishment(confirm.id);
        toast.showSuccess('Replenishment voided');
      }
      setConfirm(null);
      await refresh();
    } catch (e) {
      toast.showApiError(e, confirm.kind === 'close' ? 'Failed to close fund' : 'Failed to void replenishment');
    } finally {
      setConfirming(false);
    }
  };

  const titles: Record<string, string> = {
    create: 'New petty-cash fund', edit: 'Edit fund', replenish: 'Replenish fund', count: 'Cash count', voidVoucher: 'Void voucher',
  };
  const cashAccounts: Account[] = ref.assets.filter(a => a.postingClass === 'CASH');

  return (
    <Box data-help-page="banking.pettyCash.page" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Petty cash</Typography>
        {canManage && <Button data-help="banking.pettyCash.new" variant="contained" startIcon={<AddIcon />} onClick={() => openDlg({ kind: 'create' })}>New fund</Button>}
      </Box>
      {loading ? <CircularProgress /> : (
        <TableContainer data-help-section="banking.pettyCash.funds" component={Paper} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell data-help="banking.pettyCash.col.fund">Fund</TableCell><TableCell data-help="banking.pettyCash.col.custodian">Custodian</TableCell><TableCell data-help="banking.pettyCash.col.float" align="right">Float</TableCell>
                <TableCell data-help="banking.pettyCash.col.balance" align="right">Balance</TableCell><TableCell data-help="banking.pettyCash.col.toReplenish" align="right">To replenish</TableCell><TableCell data-help="banking.pettyCash.col.status">Status</TableCell><TableCell data-help="banking.pettyCash.col.actions">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {funds.map(f => (
                <TableRow key={f.id} hover selected={f.id === selected}>
                  <TableCell>{f.name}</TableCell>
                  <TableCell>{f.custodianName ?? '—'}</TableCell>
                  <TableCell align="right">{formatMoney(f.floatAmount)}</TableCell>
                  <TableCell align="right">{formatMoney(f.glBalance)}</TableCell>
                  <TableCell align="right">{formatMoney(f.unreplenishedTotal)} ({f.unreplenishedCount})</TableCell>
                  <TableCell><Chip size="small" label={humanizeEnum(f.status)} color={f.status === 'ACTIVE' ? 'success' : 'default'} /></TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Button data-help="banking.pettyCash.action.open" size="small" onClick={() => setSelected(f.id)}>Open</Button>
                      {canManage && f.status === 'ACTIVE' && (
                        <>
                          <Button data-help="banking.pettyCash.action.edit" size="small" onClick={() => openDlg({ kind: 'edit', fund: f })}>Edit</Button>
                          <Button data-help="banking.pettyCash.action.replenish" size="small" onClick={() => openDlg({ kind: 'replenish', fund: f })}>Replenish</Button>
                          <Button data-help="banking.pettyCash.action.count" size="small" onClick={() => openDlg({ kind: 'count', fund: f })}>Count</Button>
                          <Button data-help="banking.pettyCash.action.close" size="small" color="warning" onClick={() => setConfirm({ kind: 'close', fund: f })}>Close</Button>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {funds.length === 0 && <TableRow><TableCell colSpan={8} align="center">No petty-cash funds.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {detail && (
        <Box data-testid="fund-detail">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Typography variant="h6">{detail.fund.name}: vouchers</Typography>
            {detail.fund.status === 'ACTIVE' && <Button data-help="banking.pettyCash.addVoucher" variant="outlined" startIcon={<AddIcon />} onClick={() => setVoucherOpen(true)}>Add voucher</Button>}
          </Box>
          <TableContainer data-help-section="banking.pettyCash.vouchers" component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell data-help="banking.pettyCash.v.col.number">No.</TableCell><TableCell data-help="banking.pettyCash.v.col.date">Date</TableCell><TableCell data-help="banking.pettyCash.v.col.expense">Expense</TableCell>{detail.vouchers.some(x => x.departmentId) && <TableCell data-help="banking.pettyCash.v.col.department">Department</TableCell>}{detail.vouchers.some(x => x.projectId) && <TableCell data-help="banking.pettyCash.v.col.project">Project</TableCell>}<TableCell data-help="banking.pettyCash.v.col.payee">Payee</TableCell>
                  <TableCell data-help="banking.pettyCash.v.col.amount" align="right">Amount</TableCell><TableCell data-help="banking.pettyCash.v.col.status">Status</TableCell><TableCell data-help="banking.pettyCash.v.col.actions">Receipts / actions</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {detail.vouchers.map(v => (
                  <TableRow key={v.id}>
                    <TableCell>{v.voucherNumber}</TableCell><TableCell>{formatDate(v.voucherDate)}</TableCell>
                    <TableCell>{v.expenseAccountLabel}</TableCell>{detail.vouchers.some(x => x.departmentId) && <TableCell data-testid="voucher-department">{departmentNameOf(departmentOptions, v.departmentId)}</TableCell>}{detail.vouchers.some(x => x.projectId) && <TableCell data-testid="voucher-project">{projectName(v.projectId)}</TableCell>}<TableCell>{v.payee ?? '—'}</TableCell>
                    <TableCell align="right">{formatMoney(v.amount)}</TableCell>
                    <TableCell>{humanizeEnum(v.status)}{v.voidReason && <Typography variant="caption" display="block">{v.voidReason}</Typography>}</TableCell>
                    <TableCell>
                      <ReceiptButtons voucherId={v.id} canUpload={v.status !== 'VOIDED'} />
                      {canManage && v.status === 'POSTED' && (
                        <Button data-help="banking.pettyCash.action.voidVoucher" size="small" color="error" onClick={() => openDlg({ kind: 'voidVoucher', fund: detail.fund, voucherId: v.id })}>Void</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {detail.vouchers.length === 0 && <TableRow><TableCell colSpan={7} align="center">No vouchers.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" sx={{ mb: 1 }}>Replenishments</Typography>
          <TableContainer data-help-section="banking.pettyCash.replenishments" component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead><TableRow><TableCell data-help="banking.pettyCash.r.col.number">No.</TableCell><TableCell data-help="banking.pettyCash.r.col.date">Date</TableCell><TableCell data-help="banking.pettyCash.r.col.through">Through</TableCell>
                <TableCell data-help="banking.pettyCash.r.col.amount" align="right">Amount</TableCell><TableCell data-help="banking.pettyCash.r.col.status">Status</TableCell><TableCell data-help="banking.pettyCash.r.col.actions" /></TableRow></TableHead>
              <TableBody>
                {detail.replenishments.map(r => (
                  <TableRow key={r.id}>
                    <TableCell>{r.replenishmentNumber}</TableCell><TableCell>{formatDate(r.replenishmentDate)}</TableCell>
                    <TableCell>{formatDate(r.throughDate)}</TableCell><TableCell align="right">{formatMoney(r.amount)}</TableCell>
                    <TableCell>{humanizeEnum(r.status)}</TableCell>
                    <TableCell>{canManage && r.status === 'POSTED' && <Button data-help="banking.pettyCash.action.voidReplenishment" size="small" color="error" onClick={() => setConfirm({ kind: 'voidRepl', id: r.id, number: r.replenishmentNumber })}>Void</Button>}</TableCell>
                  </TableRow>
                ))}
                {detail.replenishments.length === 0 && <TableRow><TableCell colSpan={6} align="center">None.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" sx={{ mb: 1 }}>Cash counts</Typography>
          <TableContainer data-help-section="banking.pettyCash.counts" component={Paper}>
            <Table size="small">
              <TableHead><TableRow><TableCell data-help="banking.pettyCash.c.col.date">Date</TableCell><TableCell data-help="banking.pettyCash.c.col.counted" align="right">Counted</TableCell>
                <TableCell data-help="banking.pettyCash.c.col.gl" align="right">GL balance</TableCell><TableCell data-help="banking.pettyCash.c.col.difference" align="right">Difference</TableCell><TableCell data-help="banking.pettyCash.c.col.notes">Notes</TableCell></TableRow></TableHead>
              <TableBody>
                {detail.counts.map(c => (
                  <TableRow key={c.id}>
                    <TableCell>{formatDate(c.countDate)}</TableCell><TableCell align="right">{formatMoney(c.countedTotal)}</TableCell>
                    <TableCell align="right">{formatMoney(c.glBalance)}</TableCell>
                    <TableCell align="right" sx={{ color: c.difference === 0 ? undefined : 'error.main' }}>{formatMoney(c.difference)}</TableCell>
                    <TableCell>{c.notes ?? ''}</TableCell>
                  </TableRow>
                ))}
                {detail.counts.length === 0 && <TableRow><TableCell colSpan={5} align="center">None.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <VoucherDialog open={voucherOpen} funds={detail ? [detail.fund] : []} fundId={detail?.fund.id} expenses={ref.expenses} segments={ref.segments}
        onClose={() => setVoucherOpen(false)} onSaved={() => { setVoucherOpen(false); refresh(); }} />

      <Dialog open={!!dlg} onClose={() => setDlg(null)} maxWidth="sm" fullWidth PaperProps={dlg ? PC_DLG_HELP[dlg.kind].paper as any : undefined}>
        <DialogTitle>{dlg ? titles[dlg.kind] : ''}{dlg && dlg.kind !== 'create' ? `: ${dlg.fund.name}` : ''}</DialogTitle>
        {dlg && (
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }} data-help-section="bank.pcDialog.section.body">
            {(dlg.kind === 'create' || dlg.kind === 'edit') && (
              <>
                <TextField data-help="bank.pcFund.field.name" label="Fund name" value={name} required fullWidth onChange={e => setName(e.target.value)} />
                <EmployeeAutocomplete data-help="bank.pcFund.field.custodian" label="Custodian" value={custodian} required onChange={v => setCustodian(v)} />
                {dlg.kind === 'create' && (
                  <>
                    <AccountAutocomplete data-help="bank.pcCreate.field.glAccount" accounts={cashAccounts.length > 0 ? cashAccounts : ref.assets} value={glId}
                      label="Cash account (GL)" required onChange={id => setGlId(id)} />
                    <TextField data-help="bank.pcCreate.field.float" label="Float amount" type="number" value={floatAmount} required fullWidth onChange={e => setFloatAmount(e.target.value)} />
                    <SearchableSelect<BankAccount> data-help="bank.pcCreate.field.bank" label="Funded from bank account (optional)" options={ref.banks.filter(b => !b.currencyCode)} value={bankId}
                      getOptionLabel={bankLabel} onChange={id => setBankId(id)} />
                    <TextField data-help="bank.pcCreate.field.floatDate" label="Float date" type="date" value={floatDate} fullWidth InputLabelProps={{ shrink: true }} onChange={e => setFloatDate(e.target.value)} />
                  </>
                )}
                <TextField data-help="bank.pcFund.field.limit" label="Per-voucher limit" type="number" value={limit} required fullWidth onChange={e => setLimit(e.target.value)} />
                <SearchableSelect<Segment> data-help="bank.pcFund.field.segment" label="Segment" options={ref.segments} value={segmentId} getOptionLabel={segmentLabel} onChange={id => setSegmentId(id)} />
                <TextField data-help="bank.pcFund.field.remarks" label="Remarks" value={remarks} fullWidth multiline minRows={2} onChange={e => setRemarks(e.target.value)} />
              </>
            )}
            {dlg.kind === 'replenish' && (
              <>
                <TextField data-help="bank.pcReplenish.field.through" label="Through date" type="date" value={throughDate} fullWidth InputLabelProps={{ shrink: true }}
                  onChange={e => setThroughDate(e.target.value)} />
                {proposal && (
                  <Typography data-testid="proposal">
                    Proposed amount {formatMoney(proposal.amount)} from {proposal.voucherCount} voucher(s)
                  </Typography>
                )}
                <SearchableSelect<BankAccount> data-help="bank.pcReplenish.field.bank" label="Pay from bank account" options={ref.banks.filter(b => !b.currencyCode)} value={bankId} required
                  getOptionLabel={bankLabel} onChange={id => setBankId(id)} />
                <TextField data-help="bank.pcReplenish.field.date" label="Date" type="date" value={date} fullWidth InputLabelProps={{ shrink: true }} onChange={e => setDate(e.target.value)} />
                <TextField data-help="bank.pcReplenish.field.notes" label="Notes" value={notes} fullWidth multiline minRows={2} onChange={e => setNotes(e.target.value)} />
              </>
            )}
            {dlg.kind === 'count' && (
              <>
                <Typography variant="body2" color="text.secondary">GL balance {formatMoney(dlg.fund.glBalance)}</Typography>
                <TextField data-help="bank.pcCount.field.date" label="Count date" type="date" value={date} fullWidth InputLabelProps={{ shrink: true }} onChange={e => setDate(e.target.value)} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {DENOMS.map(d => (
                    <TextField data-help="bank.pcCount.field.denom" key={d} label={`${d} x`} type="number" size="small" value={counts[d] ?? ''} sx={{ flex: '1 1 110px', maxWidth: 140 }}
                      inputProps={{ min: 0, 'aria-label': `Notes of ${d}` }}
                      onChange={e => setCounts(c => ({ ...c, [d]: e.target.value }))} />
                  ))}
                </Box>
                {usedDenoms
                  ? <Typography data-testid="denom-total">Counted total {formatMoney(denomTotal)}</Typography>
                  : <TextField data-help="bank.pcCount.field.total" label="Counted total (if no breakdown)" type="number" value={counted} fullWidth onChange={e => setCounted(e.target.value)} />}
                <FormControlLabel data-help="bank.pcCount.field.postAdj" control={<Checkbox checked={postAdj} onChange={e => setPostAdj(e.target.checked)} />}
                  label="Post the difference to the cash over/short account" />
                <TextField data-help="bank.pcCount.field.notes" label="Notes" value={notes} fullWidth multiline minRows={2} onChange={e => setNotes(e.target.value)} />
              </>
            )}
            {dlg.kind === 'voidVoucher' && (
              <TextField data-help="bank.pcVoidVoucher.field.reason" label="Reason" value={reason} required fullWidth multiline minRows={2} onChange={e => setReason(e.target.value)} />
            )}
          </DialogContent>
        )}
        <DialogActions>
          <Button data-help="bank.pettyCash.dlgClose" onClick={() => setDlg(null)}>Close</Button>
          <Button {...helpElement(dlg ? PC_DLG_HELP[dlg.kind].helpKey : undefined)} variant="contained" disabled={saving || !valid} onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog open={!!confirm} confirmColor="error" submitting={confirming}
        paperProps={PC_CONFIRM_HELP[confirm?.kind === 'close' ? 'close' : 'void'].paper}
        confirmProps={PC_CONFIRM_HELP[confirm?.kind === 'close' ? 'close' : 'void'].confirm}
        cancelProps={PC_CONFIRM_HELP[confirm?.kind === 'close' ? 'close' : 'void'].cancel}
        title={confirm?.kind === 'close' ? 'Close fund' : 'Void replenishment'}
        confirmLabel={confirm?.kind === 'close' ? 'Close fund' : 'Void'}
        message={confirm?.kind === 'close'
          ? `Close fund ${confirm.fund.name}? A closed fund cannot take new vouchers.`
          : confirm ? `Void replenishment ${confirm.number}? Its journal entry will be reversed and the vouchers return to unreplenished.` : ''}
        onCancel={() => setConfirm(null)} onConfirm={runConfirmed} />
    </Box>
  );
};

export default PettyCash;
